import { APIClient, HTTPClient } from '@innerhive-internal/innerhive-api-client';
import { CompositeAnonymousHttpClient, VIEWER_JWT_EXPIRED } from '../auth/anonymous/CompositeAnonymousHttpClient';
import { IMediaTokenSession, IMediaTokenSessions } from './MediaTokenSessions';

const isUnauthorized = (err: unknown): boolean => err instanceof Object && 'status' in err && err.status === 401;

const isExpiredViewerSession = (err: unknown): boolean => err instanceof Object && 'errorCode' in err && err.errorCode === VIEWER_JWT_EXPIRED;

// Mints through the session serving the media's href, with that session's own entry and mediaToken
// link. Going through the app-wide client instead routes '/' and '/media-tokens' to the default
// session ('*', else the most recent), not the care map being shown.
export class AnonymousViewerMediaTokenSessions implements IMediaTokenSessions {
    static inject = () => [CompositeAnonymousHttpClient];
    constructor(private anonymousHttpClient: Pick<CompositeAnonymousHttpClient, 'getSessionServingUrl' | 'recoverFromUnauthorized'>) {}

    // One per session client, so each keeps APIClient's cached entry instead of fetching '/' per mint.
    private apiClients = new WeakMap<HTTPClient, APIClient>();

    getSessionServing = (href: string): IMediaTokenSession => {
        const { sessionToken } = this.anonymousHttpClient.getSessionServingUrl(href);
        return {
            key: sessionToken,
            apiClient: {
                entry: () => this.sendRecoveringOnce({ href, sessionToken, send: apiClient => apiClient.entry() }),
                post: (link, data) => this.sendRecoveringOnce({ href, sessionToken, send: apiClient => apiClient.post(link, data) })
            }
        };
    };

    // The mint bypasses the composite client's request() and so its 401 recovery: it recovers here
    // and retries through the re-created client.
    private sendRecoveringOnce = async <T>(params: { href: string; sessionToken: string; send: (apiClient: APIClient) => Promise<T> }): Promise<T> => {
        const { href, sessionToken, send } = params;
        const { client } = this.anonymousHttpClient.getSessionServingUrl(href);
        try {
            return await send(this.apiClientFor(client));
        } catch (err: unknown) {
            if (isExpiredViewerSession(err) || !isUnauthorized(err)) {
                throw err;
            }
            await this.anonymousHttpClient.recoverFromUnauthorized(client);

            const recovered = this.anonymousHttpClient.getSessionServingUrl(href);
            if (recovered.sessionToken !== sessionToken) {
                throw new Error('a different session serves this media after recovering, so a token minted now would be cached under the wrong one');
            }
            return await send(this.apiClientFor(recovered.client));
        }
    };

    private apiClientFor = (client: HTTPClient): APIClient => {
        let apiClient = this.apiClients.get(client);
        if (!apiClient) {
            apiClient = new APIClient(client);
            this.apiClients.set(client, apiClient);
        }
        return apiClient;
    };
}
